import { assign, createMachine, sendTo, raise, Receiver } from 'xstate';
import { billingMachine, billingContext, BillingEvent } from '~/machines/billing-selection';
import {
  enrollmentMachine,
  enrollmentContext,
  EnrollmentEvent,
} from '~/machines/enrollment-selection';
import { uploadMachine, uploadContext } from '~/machines/upload';
import { userContext, userMachine } from '~/machines/user-creation';
import { utilityContext, utilityMachine } from '~/machines/utility-selection';
import { LDClient, LDFlags } from '~/providers';
import { fetchBillingExperienceOptions } from '~/services/';
import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import { BlockedStates } from '~/utils/constants';
import { removeKeys } from '~/utils/for-warbler';
import { getEnrollmentOptions } from '~/utils/get-enrollment-options';
import { communitySolarFunnelRedirects } from '~/utils/redirects';
import { identify } from '~/utils/tracking';
import { sendEmail } from './emailer';
import {
  TelesalesFunnelContext,
  TelesalesFunnelEvent,
  TelesalesFunnelParams,
  TelesalesFunnelServices,
} from './types';

export const telesalesMachine = (params: TelesalesFunnelParams) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlgBd0AncgYgG0AGAXUVAAcB7WXc3T-GxAAPRAGYArAHYSADgBMsgIxjZjWVIAsATm2NNAGhABPREqkSAbCTG2xjS-KX6pYzQF93RtFjyFSFNR09EqsSCBcPHwCQqIIkjIKyqrqWrr6RqYIShoSJJqy2lZi8pqOGpae3hg4BMQkAK58ADa8xrQQAmAkBABunADW3U24reTGALK+dUxhHNy8-ILhcRLm+ZaMRbbaspqM8hKZZtpO+YVWSvLaYpZ6ElUgPrX+jbBgVB1dPfj9Q28fKYvMCzISRRYxFbiaRyRQqNQaHR6QwmRBOJRKEiWTQSZQSTRSRj2bGPZ5+eoNd6fTqEH5-YZUoHkkJzCILaLLUBxBKw5IItLI44IeSHTQ2Ip7bFJEWkmrk0iUj60ADCAAkAIIAOQA4gBRAD6AFUACoASQAMqbjQBNUHhcEc2LQxJwlKI9IorLyRhKPIOKSWWSyCTFMRXWXTV4NdjNTjoCBfWl9QbDGNxiBMmYsMHspZO7KMNb5MQWeSbKQYrZCpxuLF7IM+ywSLbmCPA0gAMzA5BwuvwVE4zWaqDA+HIAHl2BzYInusn-l2e9g+wOhyOx5Pp3b5lE81DshXtCRCwSidpNJWtNWRTIDuoJEV4aoxG35SRF73+4Ph6OJ1OljONJzr8Kbvt2n6rj+G7-gIsAsjmu6QlyZiHse+KEmI56Xp6ZiaDix5lEGuKyHc+yVF4TxynUnbgcuX5rr+m4AbQHwDlQJAxug5AdpwVCoGBS4rt+65-lu2b2rmSEiChShHieGFYc4V6osKwZitoShlGWhyqNIr7UQJOAAGINPghDNAAClgQzkAA6ngzRgKa+DqpgmCcKZ5Cag0qAAEYfEJDFjrOdKgR+2AmWZYCWdZ3bbmyiGctJ8SuPIJBSMopQVAoVYqdcR4ZXojjeiolhaPprzhZF5lWZgNn2aMTkuW5Hljt5fkBfRUF0KxvEcc0XE8XxhkRaZNWxeQ8UOnuyEIHhWzpUSmmBjoUg3EcKm4mKhyOJI3pljk2gVfUVVjdFtX1Q5TUAEKjM0hppvGgXdSF87dKdUUxXVcXiTuEJJdyqXpZlBQBjl2hCoGaV4RIYaOESTaMFIx00Uu1XnRNDWOc5t1Dg9sZPV1IksVQbH9YNvH8R943fZNv0Jf9+bzQVS2Eat61CioGlYrJOkPkGSjkdUkYnbR6NfZdjXOc9xNAaFC5i2dEs-ay01SYDJTA1coNQ7lWTmIWJAaacGiSDosMeBRZIGdTGO01jTUy7+JNk5x3GUyN4sXSrCGM-uzOLWGbPaGtkic1hWIWBI8iYbkhwo57Sve3ZV043dBBQK9IEK2jScTVNkkA+IQMZdr2XehDeUPmIx7qCWzgqFIAYJ7bysp1L+C460+CZ717Fu0NVOK59ycF4lTP7CzQcrSHHN5XcMiEoU5hN7iDhC5RIuo8Zef26n+DKpwqAxt2YBZ-Sicj-n9Nq0XCBrBl6XaHcbiIkS8jVpokhyAcliOLD0gdot2HjTSW2MD5HxPuQM+fdybu2Gq3UeN9C75mkISdKmgRR7CuHoAM1Z8SMBIM2MMTdp4EgTqOSCstvhvRIJQ4Sv5MyEDHn7Waigm4kEFvsAkZUNCnA2vrewaULAZRFIcKw8gKFE2dgAZV1JqAAIvqAASrqGRhoJgGhkaabUmpDQWX1LqCY6oLRGjNJaG0LDHT7nkFoGuzYMpbArHhZwAizAODyA3aQd5q6W2Fu2Oh0jgrqOuhMK0hiFHhNNJqIy45lHGLNOOTUhjNTKPHOac0mjNTGisTNZKe0a5hkON6Swmlbj4k5mtI8q0LxlHMA+C8UiqGyMNKE8JupInGn1NE2J8T1SJOSeqZUypxyGmyfqPRExrq6mUbk9W0IY42CuNHBwZSmw4XvsGOQZVDqKGflHJpDDgmtLCV0jpUSYlxISaaJJ+proWnNEaCy5pxzqgUXMu+BSlnFNWV-dZnNMI1xjneMGuIpCKEOUFOyvBsCuXcp5dq-lqQ0Ozt0ehUKmEgmQePfc5hZJoVPJhC8SkNk1hrnhWxJRDgYl2JC7qtkYVwtal5HySLaByMUSotRGitE6L0QYoxJjHkmgtFaW02LWHJTxXJdCZ5iVIw2RiaOcgioxw0uiWxdKRIMvILClqCLWVKhCaciJFzenXNufItJGSsk5IldY2a0qCUKXlcpfW54gUkQJGoLQuJWxWyoq8dF9LGX6raoaz4xr2mdO6ZcvpAz9RDJGWMrpkzpmzPtXkuITr5JyuwkKClx5S6HFkCUQMa0tW-h1Xq+F4aOqRpOdGs1Vz+k3OSfcjJTyXlvI+fmHNsqiX5pUqcTERTmxlQKOeEkAat6BOaWOatXd8bpnPqBYNIlMW9txahXNg6SXVjPNsso+hdjBj0jOgJ66q0wqXY9BMHKlGqPUZo-U2jdH6MMcY0xIqLHitVig7d+Ld2KQVZzFw4pCylKDEvf1-i3xXoXTeu6y74zssbWcmNPSW0JqtekzJ8i7X-pxY6ndA6QNurMBIzhQiY6FDWk2WQlbEO6tvQTe96HTVdKw-GttibhmjPGWmmZW6SNAbI66xV+J8i6F0MvDQVw-5MehSx5Dd60NtIw82njtyO3Cuea895mb5kHjE4S8jGyFBilcF-NapbVrhgvW+d4+AIDKLgD5MAMjcBQHwNGXUGBRjVsNC0Noq7-jOYgAAWioO5kckWeA+ejJFsAAXmiRYAO4wsiyMMYxgRPJQOmlVVul7ABkOJzJVWIfRWFIUSVQyNHMGQi252AHmvOJfYP59AgWYXBdGKF2BA8PYRei7FsA8XvO+fYMl1LGWss5baPluIhWjYOFo7DJG-9OYV04fR88uhrhrT8ZvAJ4Uu4Z11MIdgHxcCjkwGAJisEwvvVoudnul3rtUFu-ge7j38BwSM3fFbxXgyla23la4eRwWYVkwqooG9raVVe+nd7V2bt3YezBf7z2RpvagB99HP3MdblCL7B1BXSlFbWyVzb5WIcHE4TiDQooCjoWAUuPHBOvsY7+4BFFF8zso-x2j7nRPef0HkERyVy3Kerd2qD2nbjhSqCPEIq4hJCSlXZzgTnIvvu-axzOQbA14FDw50Lrn+vicASW2iWXIONtlaV04KT2IwVlArBlPCCdfJC5x77ocGdN2A-zF8sdJS1kVLyh7rEkhdgZRKFpY7iP6gB+7pnB9XLn28vfQKr9jzdPRO1Lb4UWg0pFIJPlGlshP4KDkGoUUthbFNx937qNmmuNxote2h5ReS9h+WRHv5UevSBmsLibEuxbHYlgydpzo5XNjfa1NrrPWVOB57jjkbMXWtxYS1Nmb3W0uZd1ZFtPGcS-A+pwrp3+DbA2Ab2sYMFY8QJ2a0vybfnUuLr98bimw1t8xsJsOtD9Rg5tT9z8e5L97dr9HdwdR8ywjZcQSwNJZIFBSgE53Jj5HJoF715FH1uUX031+VP0hV9QjI0kJhyDol1RHkZFlRVF5ES8A4MJlo9hZ4w48pMI0oDpGA6tMIFUXxGtXgsCoFIB1MTVzlO9zVW1bkRkJhnldRjRdQS9iFrA+CMpLglUNB8FrgH8kY6NdhbgEdA16h39d9PNP9OtUsjIBxUAjICB0BmgZFMAYtRwt8F9RsLDgCD8Usj9IsOw7CAjHC0tYBXCwBRxmDJ5A42D2ZOCvRYZrAdgrhMpSlZIGs4MmsF8Ws2srDV9mhbCj4HD8AnCXC3D8AXY+ohsADPCd8PMfCks-CwDAij5giSjQjwjIiQ9-ZojWDg5Q5ndC0AxnA+CSI3AK1Hh8BOAIA4AhAU8iAycs1EBItLAhRIs8gZNNitiNIHhhCzDKAaBFjjNRQn5AxTgdBNhSlbBOYSIZAqV+FaNzwE4Ftxgji75wVMRfRHAMpEY1UldZIbh0oSETxzx8QrBniqQ3imYP48p1AxQiQdBvQcQNJ7AhDMiow70oT9wPjOEJEfjmw-jqxKxOF6wCh1BUCVBtc6J51RIAIsTHUywa55IvjpBY5rxBY0JdBESkZZJfQqSvZMZ94mUDV60nYxx6T8kQ4xRal-Qm5CJbEhQQwCoJEloyxLBVBk9TDt5Ror494O5WN0wxTyAJTuQp8SBShAxJR9oa8VJSoZAaURRfQsFJBdj0TRZc5dSwFHYgljSJJiNJSSJ8gtgSwLAYcipw51h6kdBeTfQ8JNTZ1EFBT9ShcTTxAuSgz-4eSx9ShqwyogVyTlBJ9fQ3B+Td4vTnJD5sDT5Uz759hCFMEQ5YYGxmxnc1h1DWZo5VA-4mwlMaywwww5cspvE1sAVASLxThNhbACRdhXS58DIENlMa1mVEUPgaz1cZApQv5DpAwMhbSrhMRsRCxmcg59g0S5yg0fSf88ZMS-TpczBm8sQCgtzCgdzFVGxGdCwflDzTg39siP8Ot8igsQtXjbzydlsSpByCTTxYYKsGdNIPEy9bgfy9jtTddPsrdecayRQfRIKDhoKxAiSgw6wLiRRVBvRGMUKSBICoA+yvjcTXA7hfRS054EjH50JMEMRJAfQjpKLzDciALv8kMN8aLQKljhQILVUoLCQYKq58VjY3BfQWzxjMDIEcDIAayCgtlll9BoMch1T8EVAiEOzaNuzZz5iyA-yLDl8v8j9Cj7CQiyiIikpb5Q9dBMQQwSIVADZnxBi9D7A+D7grBS1JFKLTIwA0dMBcCazNgLwiFMJBYSJVkdC8pS0NzuKrhsLFLPBPAgA */
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as TelesalesFunnelContext,
        events: {} as TelesalesFunnelEvent,
        services: {} as TelesalesFunnelServices,
      },
      context: {
        queryParams: {},
        funnelId: params.id,
      },
      id: params.id,
      entry: ['updateTrackingIdentity', 'toggleLanguage'],
      // top-level running service that can receive and send events and make API calls (since we don't want emails to the funnel machine state)
      invoke: { id: 'emailer', src: 'emailer' },
      on: {
        SEND_EMAIL: { actions: 'sendToEmailer' },
        EMAIL_RESULT: { actions: 'setEmailResult' },
        CLEAR_EMAIL: { actions: 'unsetEmailResult' },
      },
      initial: params.initialState || 'start',
      states: {
        start: {
          always: [
            { cond: 'isBlockSignupWhenResuming', target: 'csUnavailable' },
            {
              cond: 'isResuming',
              target: 'getEnrollmentOptions',
              actions: ['setResumingData', 'updateTrackingIdentity'],
            },
            { target: 'utility' },
          ],
        },
        utility: {
          invoke: {
            id: 'utilityMachine',
            src: 'utilityMachine',
            data: utilityContext(funnelContext => ({
              queryParams: funnelContext.queryParams,
              redirect: communitySolarFunnelRedirects,
              ...params?.utility?.context,
            })),
            onDone: [
              {
                cond: 'isSignupUnavailable',
                target: 'csUnavailable',
                actions: 'setUtilitySelection',
              },
              { target: 'user', actions: 'setUtilitySelection' },
            ],
          },
        },
        user: {
          entry: 'updateTrackingIdentity',
          invoke: {
            id: 'userMachine',
            src: 'userMachine',
            data: userContext(funnelContext => ({
              funnelId: params.id,
              queryParams: funnelContext.queryParams,
              requirePhone: true,
              hidePassword: true,
              hideTerms: true,
              showInviteCode: true,
              utility: funnelContext.utilitySelection!,
              zipCode: funnelContext.zipCode!,
              ...params?.user?.context,
            })),
            onDone: [
              {
                cond: 'shouldUploadDocs',
                target: 'upload',
                actions: 'setFunnelPacket',
              },
              { target: 'getEnrollmentOptions', actions: 'setFunnelPacket' },
            ],
          },
          on: {
            CHANGE_UTILITY: { target: 'utility', actions: 'clearUtilitySelection' },
          },
        },
        upload: {
          id: 'upload',
          invoke: {
            id: 'uploadMachine',
            src: 'uploadMachine',
            data: uploadContext(funnelContext => ({
              funnelPacket: funnelContext.funnelPacket!,
            })),
            onDone: { target: 'getEnrollmentOptions' },
          },
        },
        getEnrollmentOptions: {
          entry: [
            assign((context: TelesalesFunnelContext) => {
              const enrollmentOptions = getEnrollmentOptions({
                preference: context.queryParams.preferredEnrollment,
                utilityId: context.funnelPacket!.utility.id,
              });
              return enrollmentOptions;
            }),
          ],
          always: [
            {
              cond: 'shouldAdvanceOnResuming',
              target: 'fetchBillingExperienceOptions',
              actions: 'setEnrollmentSelectionAsUtilityConnect',
            },
            { target: 'enrollment' },
          ],
        },
        enrollment: {
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              funnelPacket: funnelContext.funnelPacket!,
              enrollmentOptions: funnelContext.enrollmentOptions,
              startAtAccountNumber: LDClient.variation(LDFlags.showUtiltityAccountNumberScreen),
              advanceOn: ['verified', 'timed_out'],
              ...params?.enrollment?.context,
            })),
            onDone: [
              { target: 'fetchBillingExperienceOptions', actions: 'setEnrollmentSelection' },
            ],
          },
          on: {
            UPDATED_USER: {
              actions: ['updateUser', 'sendToEnrollment'],
            },
          },
        },
        fetchBillingExperienceOptions: {
          invoke: {
            id: 'fetchBillingExperienceOptions',
            src: 'fetchBillingExperienceOptions',
            onDone: [
              {
                cond: 'onlyUcb',
                target: 'completed',
                actions: [
                  'setBillingExperienceOptions',
                  'setBillingSelectionAsUcb',
                  'sendTermsEmail',
                ],
              },
              {
                cond: 'excludeBillingStep',
                target: 'completed',
                actions: ['setBillingExperienceOptions', 'sendTermsEmail'],
              },
              { target: 'billing', actions: 'setBillingExperienceOptions' },
            ],
            onError: 'unexpected',
          },
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              billingOptions: funnelContext.billingExperienceOptions,
              funnelPacket: funnelContext.funnelPacket!,
              ...params?.billing?.context,
            })),
            onDone: {
              target: 'completed',
              actions: ['setBillingSelection', 'sendTermsEmail'],
            },
          },
          on: {
            UPDATED_USER: {
              actions: ['updateUser', 'sendToBilling'],
            },
          },
        },
        completed: {
          id: 'completed',
          on: {
            UPDATED_USER: { actions: 'updateUser' },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
        csUnavailable: {
          id: 'csUnavailable',
        },
      },
    },
    {
      actions: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
        // user info updated, so tell the billing machine about it
        sendToBilling: sendTo(
          'billingMachine',
          (context, _event): BillingEvent => ({
            type: 'UPDATED_CONTEXT_FROM_PARENT',
            data: { funnelPacket: context.funnelPacket },
          }),
        ),
        // user info updated, so tell the enrollment machine about it
        sendToEnrollment: sendTo(
          'enrollmentMachine',
          (context, _event): EnrollmentEvent => ({
            type: 'UPDATED_CONTEXT_FROM_PARENT',
            data: { funnelPacket: context.funnelPacket },
          }),
        ),
        // new email requested, so send to the emailer service
        sendToEmailer: sendTo(
          'emailer',
          (context, event): TelesalesFunnelEvent => ({
            type: 'SEND_EMAIL_FROM_EMAILER_SERVICE',
            key: event.key,
            userId: context.funnelPacket!.user.id,
          }),
        ),
        // raise === send event to self
        sendTermsEmail: raise({ type: 'SEND_EMAIL', key: 'ResumeAtTerms' }),
        setEmailResult: assign((_context, event) => ({
          emailResult: event.data,
        })),
        unsetEmailResult: assign((_context, _event) => ({
          emailResult: undefined,
        })),
        clearUtilitySelection: assign(context => ({
          utilitySelection: undefined,
          queryParams: removeKeys(context.queryParams, ['utilityId', 'zipCode']),
        })),
        setBillingExperienceOptions: assign((_context, event) => ({
          billingExperienceOptions: event.data.billingExperienceOptions,
        })),
        setBillingSelection: assign((_context, event) => ({
          billingSelection: event.data.billingSelection,
          paymentMethod: event.data.selectedPaymentMethod,
        })),
        setBillingSelectionAsUcb: assign(_context => ({
          billingSelection: 'ucb' as BillingDataSource,
        })),
        setEnrollmentSelection: assign((_context, event) => ({
          enrollmentSelection: event.data.enrollmentSelection,
        })),
        setEnrollmentSelectionAsUtilityConnect: assign(_context => ({
          enrollmentSelection: 'utility_connect' as EnrollmentDataSource,
        })),
        setFunnelPacket: assign((_context, event) => ({
          funnelPacket: event.data.funnelPacket,
        })),
        setUtilitySelection: assign((_context, event) => ({
          utilitySelection: event.data.utility,
          zipCode: event.data.zipCode,
        })),
        setResumingData: assign(context => ({
          funnelPacket: context.queryParams.resumingUser!.packet,
        })),
        updateTrackingIdentity: context => {
          const packet = context.funnelPacket || context.queryParams.resumingUser?.packet;
          if (packet) {
            identify({
              email: packet.user.email,
              id: packet.user.id,
              utilityId: packet.utility.id,
              funnelTrack: packet.utility.funnelTrack,
              state: packet.utility.state.abbreviation,
            });
          } else {
            identify({
              utilityId: context.utilitySelection?.id,
              funnelTrack: context.utilitySelection?.funnelTrack,
            });
          }
        },
        updateUser: assign((context, event) => ({
          funnelPacket: {
            ...context.funnelPacket!,
            user: { ...context.funnelPacket!.user, ...event.data },
          },
        })),
        toggleLanguage: context => {
          const spanishPromoCodes = [
            'spanish',
            'spanish25',
            'spanish50',
            'spanish75',
            'spanish100',
          ];
          const { promoCode } = context.queryParams;
          if (promoCode && spanishPromoCodes.includes(promoCode)) {
            window.Localize?.setLanguage('es');
          }
        },

        /* eslint-disable @typescript-eslint/no-unused-vars */
      },
      guards: {
        onlyUcb: (_context, event) =>
          event.data.billingExperienceOptions.length === 1 &&
          event.data.billingExperienceOptions.includes('ucb'),
        excludeBillingStep: () => !!params.excludeBillingStep,
        isResuming: context => !!context.queryParams?.resumingUser,
        shouldAdvanceOnResuming: context =>
          context.funnelPacket!.utilityCredential.verificationStatus! === 'correct',
        shouldUploadDocs: context => {
          const lmiProofDocsStates = LDClient.variation(LDFlags.lmiProofDocsState);
          return lmiProofDocsStates?.includes(context.utilitySelection!.state.abbreviation);
        },
        isBlockSignupWhenResuming: context => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          return shouldBlockSignups && isResumingUserInBlockedStates(context, params);
        },
        isSignupUnavailable: (context, event) => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          const utilityStateAbbreviation =
            context.utilitySelection?.state.abbreviation || event?.data?.utility.state.abbreviation;
          const isBlockedState = BlockedStates(params.id).includes(utilityStateAbbreviation);

          return shouldBlockSignups && isBlockedState;
        },
      },
      services: {
        billingMachine: billingMachine(),
        enrollmentMachine: enrollmentMachine().withConfig({
          guards: {
            shouldAdvance: context =>
              LDClient.variation(LDFlags.accountNumberBackup)
                ? ['verified'].includes(context.utilityConnectStatus!)
                : ['verified', 'timed_out', 'error'].includes(context.utilityConnectStatus!),
            shouldFallBackToAccountNumber: context =>
              LDClient.variation(LDFlags.accountNumberBackup) &&
              ['error', 'timed_out'].includes(context.utilityConnectStatus!),
          },
        }),
        uploadMachine: uploadMachine(),
        userMachine: userMachine(),
        utilityMachine: utilityMachine(),
        fetchBillingExperienceOptions: async context => {
          const { billingExperienceOptions } = await fetchBillingExperienceOptions({
            enrollmentDataSourceName: context.enrollmentSelection!,
            limit: 1,
            preference: context.queryParams.preferredBillingExperience,
            utilityId: context.funnelPacket!.utility.id,
          });
          return { billingExperienceOptions };
        },
        emailer: () => (send, onEvent: Receiver<TelesalesFunnelEvent>) => {
          onEvent(async event => {
            if (event.type === 'SEND_EMAIL_FROM_EMAILER_SERVICE') {
              const result = await sendEmail({ key: event.key, userId: event.userId });
              send({ type: 'EMAIL_RESULT', data: result });
              setTimeout(() => send({ type: 'CLEAR_EMAIL' }), 5000);
            }
          });
        },
      },
    },
  );

export const isResumingUserInBlockedStates = (context, params) => {
  const resumingUser = context.queryParams?.resumingUser;
  const utilityStateAbbreviation = resumingUser?.packet?.utility.state.abbreviation;
  const isBlockedState = BlockedStates(params.id).includes(utilityStateAbbreviation);

  return !!resumingUser && isBlockedState;
};
